import loadable from '@loadable/component';
import { Modal, Textfield, useTheme } from '@simplywallst/ui-core';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import Media from 'react-media';
import { SearchIcon } from '@/components/Search';

const AlgoliaSearch = loadable(
  () => import('@/components/Search/components/AlgoliaSearch'),
  {
    fallback: <div className="bones h-x5 w-full" />,
  }
);

const BasicSearch = loadable(
  () => import('@/components/Search/components/BasicSearch'),
  {
    fallback: <div className="bones h-x5 w-full" />,
  }
);

const PLACEHOLDER = 'Search 120k companies worldwide';

type Props = {
  onCompanySelect: (canonicalUrl: string) => void;
};

export const NarrativeMiniValuatorCompanySearch = ({
  onCompanySelect,
}: Props) => {
  const [showSearch, setShowSearch] = useState(false);
  const [canonicalUrl, setCanonicalUrl] = useState<string | null>(null);
  const theme = useTheme();

  const handleSelect = ({ canonicalUrl }: { canonicalUrl: string }) => {
    setShowSearch(false);
    setCanonicalUrl(canonicalUrl);
  };

  /**
   * When a company is selected, we need to set the canonicalUrl url first and only propage
   * the change via a lifecycle hook. BasicSearch on Desktop is using a Popper component,
   * which if we changed we unmounted the component without triggering an explicit render
   * cycle event would dettach from the DOM and would re-attach itself to document.body
   * causing a janky experience where the dropdown would briefly flash across the screen.
   */
  useEffect(() => {
    if (canonicalUrl) {
      onCompanySelect(canonicalUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canonicalUrl]);

  const getResult = (
    child: ReactNode,
    result: {
      id?: string;
      uniqueSymbol: string;
      canonicalUrl: string;
    }
  ) => (
    <div
      className="cursor-pointer"
      onClick={() => {
        handleSelect({
          canonicalUrl: result.canonicalUrl,
        });
      }}
    >
      {child}
    </div>
  );
  return (
    <Media query={{ maxWidth: theme.mdMaxRaw }}>
      {(match) => {
        if (match) {
          if (!showSearch)
            return (
              <Textfield
                prefix={<SearchIcon />}
                placeholder={PLACEHOLDER}
                type="text"
                onFocus={() => setShowSearch(true)}
                onBlur={() => setShowSearch(false)}
              />
            );
          return (
            <Modal
              isNaked
              onPrimaryClick={() => setShowSearch(false)}
              onSecondaryClick={() => setShowSearch(false)}
            >
              <AlgoliaSearch
                placeholder={PLACEHOLDER}
                onClose={() => setShowSearch(false)}
                primaryWrapper={getResult}
                secondaryWrapper={getResult}
              />
            </Modal>
          );
        }

        return (
          <div>
            <BasicSearch
              placeholder={PLACEHOLDER}
              onClick={(_uniqueSymbol, { canonicalUrl }) => {
                handleSelect({ canonicalUrl });
              }}
            />
          </div>
        );
      }}
    </Media>
  );
};
